import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { ApiError } from '@/shared/api/client';
import {
  insurerBranches,
  type InsurerBranch,
  type InsurerBranchInput,
} from '@/shared/api/resources';
import { SIGLAS_DAS_UFS } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { Select } from '@/shared/ui/Select';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../shared/fieldError';

/**
 * Aba "Filiais" da seguradora (P-MIN-2): a FILIAL que o frontispicio da apolice
 * imprime. So cadastro. A filial nao e excluida, porque pode estar numa apolice:
 * fora de uso, fica inativa, e a emissao recusa a filial inativa. Ver
 * docs/ADR/0057-documento-da-apolice.md.
 */
export function InsurerBranchesPanel({
  insurerId,
  readOnly,
}: {
  insurerId: string;
  readOnly: boolean;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [emEdicao, setEmEdicao] = useState<InsurerBranch | 'nova' | null>(null);

  const chave = ['insurer-branches', insurerId];
  const consulta = useQuery({
    queryKey: chave,
    queryFn: ({ signal }) => insurerBranches.list(insurerId, undefined, signal),
  });

  const salvar = useMutation({
    mutationFn: (input: InsurerBranchInput) =>
      emEdicao && emEdicao !== 'nova'
        ? insurerBranches.update(insurerId, emEdicao.id, input)
        : insurerBranches.create(insurerId, input),
    onSuccess: (filial) => {
      toast.success(`Filial ${filial.name} salva.`);
      setEmEdicao(null);
      void queryClient.invalidateQueries({ queryKey: ['insurer-branches'] });
    },
  });

  const colunas: Column<InsurerBranch>[] = [
    {
      key: 'nome',
      header: 'Filial',
      render: (filial) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-ink-900">{filial.name}</p>
          {filial.code ? (
            <p className="truncate text-2xs text-ink-500">Codigo {filial.code}</p>
          ) : null}
        </div>
      ),
    },
    {
      key: 'cidade',
      header: 'Cidade',
      render: (filial) =>
        [filial.city, filial.state].filter((parte) => parte !== '').join(' / ') || '-',
    },
    {
      key: 'situacao',
      header: 'Situacao',
      render: (filial) => (filial.status === 'ativa' ? 'Ativa' : 'Inativa'),
    },
  ];

  if (emEdicao !== null) {
    return (
      <BranchForm
        filial={emEdicao === 'nova' ? undefined : emEdicao}
        submitting={salvar.isPending}
        error={salvar.error}
        onCancel={() => setEmEdicao(null)}
        onSubmit={(input) => salvar.mutate(input)}
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar as filiais">
          {consulta.error.message}
        </Alert>
      ) : null}
      {!readOnly ? (
        <div className="flex justify-end">
          <Button size="sm" onClick={() => setEmEdicao('nova')}>
            Nova filial
          </Button>
        </div>
      ) : null}
      <DataTable
        columns={colunas}
        items={consulta.data ?? []}
        keyOf={(filial) => filial.id}
        loading={consulta.isPending}
        {...(readOnly
          ? {}
          : { onRowClick: (filial: InsurerBranch) => setEmEdicao(filial) })}
        empty={
          <EmptyState
            icon="shield"
            title="Nenhuma filial cadastrada"
            description="A filial sai no documento da apolice, no campo FILIAL. Sem filial, o campo fica vazio."
          />
        }
      />
    </div>
  );
}

function BranchForm({
  filial,
  submitting,
  error,
  onSubmit,
  onCancel,
}: {
  filial?: InsurerBranch | undefined;
  submitting: boolean;
  error: Error | null;
  onSubmit: (input: InsurerBranchInput) => void;
  onCancel: () => void;
}) {
  const [nome, setNome] = useState(filial?.name ?? '');
  const [codigo, setCodigo] = useState(filial?.code ?? '');
  const [cidade, setCidade] = useState(filial?.city ?? '');
  const [uf, setUf] = useState(filial?.state ?? '');
  const [situacao, setSituacao] = useState(filial?.status ?? 'ativa');
  const [observacoes, setObservacoes] = useState(filial?.notes ?? '');
  const apiError = error instanceof ApiError ? error : null;

  function submit(event: FormEvent) {
    event.preventDefault();
    onSubmit({
      name: nome,
      code: codigo,
      city: cidade,
      state: uf,
      notes: observacoes,
      ...(filial ? { status: situacao } : {}),
    });
  }

  return (
    <form
      onSubmit={submit}
      className="flex flex-col gap-4"
      aria-label="Filial da seguradora"
    >
      {error ? (
        <Alert tone="danger" title="Nao foi possivel salvar a filial">
          {error.message}
        </Alert>
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Nome da filial"
          required
          value={nome}
          maxLength={120}
          error={fieldErrorOf(apiError, 'name')}
          onChange={(event) => setNome(event.target.value)}
        />
        <Field
          label="Codigo da filial"
          value={codigo}
          maxLength={40}
          onChange={(event) => setCodigo(event.target.value)}
        />
        <Field
          label="Cidade da filial"
          value={cidade}
          maxLength={120}
          onChange={(event) => setCidade(event.target.value)}
        />
        <Select
          label="UF da filial"
          placeholder="Selecione"
          value={uf}
          options={SIGLAS_DAS_UFS.map((sigla) => ({ value: sigla, label: sigla }))}
          onChange={(event) => setUf(event.target.value)}
        />
        {filial ? (
          <Select
            label="Situacao da filial"
            value={situacao}
            options={[
              { value: 'ativa', label: 'Ativa' },
              { value: 'inativa', label: 'Inativa' },
            ]}
            onChange={(event) => setSituacao(event.target.value as 'ativa' | 'inativa')}
          />
        ) : null}
      </div>
      <Textarea
        label="Observacoes da filial"
        value={observacoes}
        onChange={(event) => setObservacoes(event.target.value)}
      />
      <div className="flex justify-end gap-2 border-t border-line pt-4">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Voltar as filiais
        </Button>
        <Button type="submit" loading={submitting}>
          Salvar filial
        </Button>
      </div>
    </form>
  );
}
